import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import TabBar from '../components/ui/TabBar';
import DataTable, { Column } from '../components/ui/DataTable';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';

const API = `${API_V1}/revenue/disclosures`;

// ── Types ──────────────────────────────────────────────────────

interface DisaggBucket { label: string; recognized: number; pct_of_total: number; contract_count: number; }
interface DisaggReport {
  asc_paragraph: string; dimension: string; mode: string;
  total_recognized: number;
  buckets: DisaggBucket[];
  tie_out: { expected_total_recognized: number; computed_bucket_sum: number; difference: number; reconciled: boolean; };
  no_netting_notice: string;
}

interface RemainingContract { contract_id: number; contract_number: string; customer_name: string; entity_code: string; total: number; within_horizon: number; beyond_horizon: number; unknown_timing: number; pob_count: number; }
interface RemainingReport {
  asc_paragraph: string; as_of: string; horizon_months: number; horizon_cutoff: string;
  total_remaining: number; within_horizon: number; beyond_horizon: number; unknown_timing: number; unknown_timing_pob_count: number;
  by_contract: RemainingContract[];
  disclosure_note: string;
}

interface RollforwardBucket { opening: number; period_debits?: number; period_credits?: number; period_movement?: number; closing: number; account_codes?: string[]; normal_balance?: string; by_source_type?: Record<string, { debits: number; credits: number; movement: number; }>; tie_out: { reconciled: boolean; difference: number; }; capitalized?: number; amortized?: number; impaired?: number; reversed?: number; negative_balance_assets?: any[]; cost_type?: string; }
interface RollforwardReport {
  from_period: string; to_period: string; entity_code: string | null;
  contract_balances: { asc_paragraph: string; from_start_date: string; to_end_date_inclusive: string; buckets: Record<string, RollforwardBucket>; disclosure_note: string; posting_date_note: string; };
  capitalized_costs: { asc_paragraph: string; from_start_date: string; to_end_date_inclusive: string; buckets: Record<string, RollforwardBucket>; disclosure_note: string; };
}

interface Judgment { topic: string; paragraph_ref: string; description: string; source_attestations: string[]; affirmed_count: number; pending_count: number; total_in_scope: number; narrative: string; }
interface Expedient { key: string; paragraph_ref: string; title: string; elected: boolean; narrative: string; }
interface JudgmentsReport {
  asc_paragraph: string; review_count: number;
  judgments: Judgment[];
  transition_expedients: Expedient[];
  disclosure_note: string;
}

// ── Utility ────────────────────────────────────────────────────

const now = new Date();
const pad = (n: number) => String(n).padStart(2, '0');
const thisYear = now.getFullYear();
const thisMonth = now.getMonth() + 1;
const defaultFromPeriod = `${thisYear}-${pad(Math.max(1, thisMonth - 2))}`;
const defaultToPeriod = `${thisYear}-${pad(thisMonth)}`;

const csvEscape = (val: any): string => {
  if (val === null || val === undefined) return '';
  const s = String(val);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

const downloadCsv = (filename: string, rows: any[], columns: { key: string; label: string }[]) => {
  const header = columns.map((c) => csvEscape(c.label)).join(',');
  const body = rows.map((r) => columns.map((c) => csvEscape(r[c.key])).join(',')).join('\n');
  const blob = new Blob([`${header}\n${body}`], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
};

const tieOutBadge = (reconciled: boolean | undefined, diff: number | undefined) => (
  <span style={{
    display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
    background: reconciled ? 'rgba(34,197,94,0.12)' : 'rgba(234,179,8,0.15)',
    color: reconciled ? 'var(--positive)' : 'var(--warning)',
  }}>
    {reconciled ? 'TIED OUT' : `DRIFT ${diff !== undefined ? formatAmount(diff) : ''}`}
  </span>
);

// ── Columns ────────────────────────────────────────────────────

const disaggCols: Column[] = [
  { key: 'label', label: 'Bucket', render: (v: string) => <span style={{ fontWeight: 600 }}>{v}</span> },
  { key: 'recognized', label: 'Recognized', align: 'right', render: (v: number) => <span style={{ color: 'var(--positive)' }}>{formatAmount(v)}</span> },
  { key: 'pct_of_total', label: '% of Total', align: 'right', render: (v: number) => <span>{v.toFixed(2)}%</span> },
  { key: 'contract_count', label: 'Contracts', align: 'right' },
];

const remainingContractCols: Column[] = [
  { key: 'contract_number', label: 'Contract' },
  { key: 'customer_name', label: 'Customer', render: (v: string) => v || '-' },
  { key: 'entity_code', label: 'Entity' },
  { key: 'pob_count', label: 'POBs', align: 'right' },
  { key: 'within_horizon', label: '≤ 12 mo', align: 'right', render: (v: number) => v > 0 ? <span style={{ color: 'var(--accent)' }}>{formatAmount(v)}</span> : <span style={{ color: 'var(--text-hint)' }}>—</span> },
  { key: 'beyond_horizon', label: '> 12 mo', align: 'right', render: (v: number) => v > 0 ? <span style={{ color: 'var(--positive)' }}>{formatAmount(v)}</span> : <span style={{ color: 'var(--text-hint)' }}>—</span> },
  { key: 'unknown_timing', label: 'Unknown', align: 'right', render: (v: number) => v > 0 ? <span style={{ color: 'var(--warning)', fontWeight: 600 }}>{formatAmount(v)}</span> : <span style={{ color: 'var(--text-hint)' }}>—</span> },
  { key: 'total', label: 'Total', align: 'right', render: (v: number) => <span style={{ fontWeight: 600 }}>{formatAmount(v)}</span> },
];

// ── Component ──────────────────────────────────────────────────

export default function RevenueDisclosures() {
  const { entity } = useGlobalEntity();
  const [tab, setTab] = useState('disaggregation');

  // Disaggregation state
  const [dimension, setDimension] = useState('recognition_method');
  const [disagg, setDisagg] = useState<DisaggReport | null>(null);
  const [disaggLoading, setDisaggLoading] = useState(false);

  // Remaining POB state
  const [remaining, setRemaining] = useState<RemainingReport | null>(null);
  const [remainingLoading, setRemainingLoading] = useState(false);

  // Roll-forward state
  const [fromPeriod, setFromPeriod] = useState(defaultFromPeriod);
  const [toPeriod, setToPeriod] = useState(defaultToPeriod);
  const [rollforward, setRollforward] = useState<RollforwardReport | null>(null);
  const [rollforwardLoading, setRollforwardLoading] = useState(false);
  const [rollforwardError, setRollforwardError] = useState<string | null>(null);

  // Judgments state
  const [judgments, setJudgments] = useState<JudgmentsReport | null>(null);
  const [judgmentsLoading, setJudgmentsLoading] = useState(false);

  const loadDisagg = useCallback(async () => {
    setDisaggLoading(true);
    try {
      const r = await authFetch(withEntityParam(`${API}/disaggregation?dimension=${dimension}`, entity));
      if (r.ok) setDisagg(await r.json());
    } catch (e) { console.error(e); }
    setDisaggLoading(false);
  }, [entity, dimension]);

  const loadRemaining = useCallback(async () => {
    setRemainingLoading(true);
    try {
      const r = await authFetch(withEntityParam(`${API}/remaining-pob`, entity));
      if (r.ok) setRemaining(await r.json());
    } catch (e) { console.error(e); }
    setRemainingLoading(false);
  }, [entity]);

  const loadRollforward = useCallback(async () => {
    setRollforwardLoading(true);
    setRollforwardError(null);
    try {
      const url = withEntityParam(`${API}/rollforward?from_period=${fromPeriod}&to_period=${toPeriod}`, entity);
      const r = await authFetch(url);
      if (r.ok) {
        setRollforward(await r.json());
      } else {
        const err = await r.json();
        setRollforwardError(err.detail || `HTTP ${r.status}`);
        setRollforward(null);
      }
    } catch (e: any) {
      setRollforwardError(e?.message || 'Network error');
    }
    setRollforwardLoading(false);
  }, [entity, fromPeriod, toPeriod]);

  const loadJudgments = useCallback(async () => {
    setJudgmentsLoading(true);
    try {
      const r = await authFetch(withEntityParam(`${API}/judgments`, entity));
      if (r.ok) setJudgments(await r.json());
    } catch (e) { console.error(e); }
    setJudgmentsLoading(false);
  }, [entity]);

  useEffect(() => { loadDisagg(); }, [loadDisagg]);
  useEffect(() => { if (tab === 'remaining') loadRemaining(); }, [tab, loadRemaining]);
  useEffect(() => { if (tab === 'rollforward') loadRollforward(); }, [tab, loadRollforward]);
  useEffect(() => { if (tab === 'judgments') loadJudgments(); }, [tab, loadJudgments]);

  const tabs = [
    { key: 'disaggregation', label: 'Disaggregation' },
    { key: 'rollforward', label: 'Roll-Forward' },
    { key: 'remaining', label: 'Remaining POB' },
    { key: 'judgments', label: 'Judgments & Expedients' },
  ];

  const ascRefBanner = (asc: string, note: string) => (
    <div style={{ padding: '10px 14px', borderRadius: 6, background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.20)', color: 'var(--text-secondary)', fontSize: 12, marginBottom: 20, lineHeight: 1.5 }}>
      <span style={{ fontWeight: 600, color: 'var(--accent)' }}>{asc}: </span>
      {note}
    </div>
  );

  return (
    <PageLayout
      title="Revenue Disclosures"
      subtitle="ASC 606-10-50 footnote pack: disaggregation, roll-forward, remaining POB, judgments"
    >
      <TabBar tabs={tabs} active={tab} onChange={setTab} />

      {tab === 'disaggregation' && (
        <>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Dimension:</label>
            <select
              aria-label="Disaggregation dimension"
              value={dimension}
              onChange={(e) => setDimension(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid var(--border-subtle)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 13 }}
            >
              <option value="contract_subtype">Contract Subtype</option>
              <option value="recognition_method">Recognition Method</option>
              <option value="timing_pattern">Timing Pattern (PIT vs Over-Time)</option>
              <option value="entity_code">Entity (Geography)</option>
            </select>
            {disagg && (
              <button
                onClick={() => downloadCsv(`disaggregation-${dimension}.csv`, disagg.buckets, disaggCols.map((c) => ({ key: c.key, label: c.label })))}
                style={{ marginLeft: 'auto', padding: '6px 12px', borderRadius: 4, border: '1px solid var(--border-subtle)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 12, cursor: 'pointer' }}
              >Download CSV</button>
            )}
          </div>
          {disaggLoading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>Loading…</div>
          ) : disagg ? (
            <>
              {ascRefBanner(disagg.asc_paragraph, disagg.no_netting_notice)}
              <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Total: <strong style={{ color: 'var(--positive)' }}>{formatAmount(disagg.total_recognized)}</strong></span>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Mode: <strong>{disagg.mode}</strong></span>
                {tieOutBadge(disagg.tie_out.reconciled, disagg.tie_out.difference)}
              </div>
              <DataTable columns={disaggCols} rows={disagg.buckets} rowKey="label" emptyMessage="No revenue disaggregation data." />
            </>
          ) : null}
        </>
      )}

      {tab === 'rollforward' && (
        <>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>From:</label>
            <input
              aria-label="From period"
              type="text"
              value={fromPeriod}
              onChange={(e) => setFromPeriod(e.target.value)}
              placeholder="YYYY-MM"
              style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid var(--border-subtle)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 13, width: 110 }}
            />
            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>To:</label>
            <input
              aria-label="To period"
              type="text"
              value={toPeriod}
              onChange={(e) => setToPeriod(e.target.value)}
              placeholder="YYYY-MM"
              style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid var(--border-subtle)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 13, width: 110 }}
            />
            <button
              onClick={loadRollforward}
              style={{ padding: '6px 12px', borderRadius: 4, border: '1px solid var(--accent)', background: 'var(--accent)', color: 'white', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
            >Run Roll-Forward</button>
          </div>
          {rollforwardLoading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>Loading…</div>
          ) : rollforwardError ? (
            <div style={{ padding: 16, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.30)', borderRadius: 6, color: 'var(--accent-critical)', fontSize: 13 }}>
              Error: {rollforwardError}
            </div>
          ) : rollforward ? (
            <>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>{rollforward.contract_balances.asc_paragraph} — Contract Balances</div>
              {ascRefBanner('Posting-date cut', rollforward.contract_balances.posting_date_note)}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12, marginBottom: 24 }}>
                {Object.entries(rollforward.contract_balances.buckets).map(([name, b]) => (
                  <div key={name} style={{ padding: 14, border: '1px solid var(--border-subtle)', borderRadius: 6, background: 'var(--bg-card)' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>{name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-hint)', marginBottom: 8 }}>Codes: {(b.account_codes || []).join(', ') || '(none)'}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, fontSize: 12 }}>
                      <div><div style={{ color: 'var(--text-hint)', fontSize: 10 }}>Opening</div><div>{formatAmount(b.opening)}</div></div>
                      <div><div style={{ color: 'var(--text-hint)', fontSize: 10 }}>Movement</div><div style={{ color: (b.period_movement || 0) >= 0 ? 'var(--positive)' : 'var(--accent-critical)' }}>{formatAmount(b.period_movement || 0)}</div></div>
                      <div><div style={{ color: 'var(--text-hint)', fontSize: 10 }}>Closing</div><div style={{ fontWeight: 600 }}>{formatAmount(b.closing)}</div></div>
                    </div>
                    <div style={{ marginTop: 8 }}>{tieOutBadge(b.tie_out?.reconciled, b.tie_out?.difference)}</div>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>{rollforward.capitalized_costs.asc_paragraph} — Capitalized Cost Assets</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
                {Object.entries(rollforward.capitalized_costs.buckets).map(([name, b]) => (
                  <div key={name} style={{ padding: 14, border: '1px solid var(--border-subtle)', borderRadius: 6, background: 'var(--bg-card)' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>{name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-hint)', marginBottom: 8 }}>{b.cost_type}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 12 }}>
                      <div>Opening: {formatAmount(b.opening)}</div>
                      <div>Capitalized: <span style={{ color: 'var(--positive)' }}>{formatAmount(b.capitalized || 0)}</span></div>
                      <div>Amortized: <span style={{ color: 'var(--accent-critical)' }}>({formatAmount(b.amortized || 0)})</span></div>
                      <div>Impaired: <span style={{ color: 'var(--accent-critical)' }}>({formatAmount(b.impaired || 0)})</span></div>
                      <div>Reversed: <span style={{ color: 'var(--positive)' }}>{formatAmount(b.reversed || 0)}</span></div>
                      <div style={{ fontWeight: 700 }}>Closing: {formatAmount(b.closing)}</div>
                    </div>
                    <div style={{ marginTop: 8 }}>{tieOutBadge(b.tie_out?.reconciled, b.tie_out?.difference)}</div>
                    {(b.negative_balance_assets?.length || 0) > 0 && (
                      <div style={{ marginTop: 8, padding: 8, background: 'rgba(234,179,8,0.10)', borderRadius: 4, fontSize: 11, color: 'var(--warning)' }}>
                        ⚠ {b.negative_balance_assets?.length} negative-balance asset(s) — reviewer action required
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </>
      )}

      {tab === 'remaining' && (
        <>
          {remainingLoading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>Loading…</div>
          ) : remaining ? (
            <>
              {ascRefBanner(remaining.asc_paragraph, remaining.disclosure_note)}
              <KpiGrid columns={4} items={[
                { label: 'Total Remaining', value: formatAmountCompact(remaining.total_remaining), color: 'var(--accent)' },
                { label: `≤ ${remaining.horizon_months} months`, value: formatAmountCompact(remaining.within_horizon), color: 'var(--positive)' },
                { label: `> ${remaining.horizon_months} months`, value: formatAmountCompact(remaining.beyond_horizon), color: 'var(--text-secondary)' },
                { label: 'Unknown Timing', value: formatAmountCompact(remaining.unknown_timing), color: remaining.unknown_timing > 0 ? 'var(--warning)' : 'var(--text-hint)' },
              ]} />
              {remaining.unknown_timing_pob_count > 0 && (
                <div style={{ marginBottom: 16, padding: 10, background: 'rgba(234,179,8,0.10)', borderRadius: 6, fontSize: 12, color: 'var(--warning)' }}>
                  ⚠ {remaining.unknown_timing_pob_count} POB(s) have unknown expected recognition timing (no schedule line + no end_date). Reviewer action: set end_date on the POB or contract to resolve.
                </div>
              )}
              <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 13, fontWeight: 700 }}>By Contract</div>
                <button
                  onClick={() => downloadCsv('remaining-pob.csv', remaining.by_contract, remainingContractCols.map((c) => ({ key: c.key, label: c.label })))}
                  style={{ padding: '6px 12px', borderRadius: 4, border: '1px solid var(--border-subtle)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 12, cursor: 'pointer' }}
                >Download CSV</button>
              </div>
              <DataTable columns={remainingContractCols} rows={remaining.by_contract} rowKey="contract_id" emptyMessage="No unsatisfied performance obligations." />
            </>
          ) : null}
        </>
      )}

      {tab === 'judgments' && (
        <>
          {judgmentsLoading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>Loading…</div>
          ) : judgments ? (
            <>
              {ascRefBanner(judgments.asc_paragraph, judgments.disclosure_note)}
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>
                Based on <strong>{judgments.review_count}</strong> reviewed contract{judgments.review_count === 1 ? '' : 's'}.
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Significant Judgments (ASC 606-10-50-17 to 50-20 + 340-40-50)</div>
              {judgments.judgments.map((j) => {
                const pct = j.total_in_scope > 0 ? (j.affirmed_count / j.total_in_scope) * 100 : 0;
                return (
                  <div key={j.topic} style={{ padding: 14, marginBottom: 10, border: '1px solid var(--border-subtle)', borderRadius: 6, background: 'var(--bg-card)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <div style={{ fontSize: 12, fontWeight: 700 }}>{j.topic.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</div>
                      <div style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 600 }}>{j.paragraph_ref}</div>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8, lineHeight: 1.5 }}>{j.description}</div>
                    <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 8, fontSize: 12 }}>
                      <span>Affirmed: <strong style={{ color: 'var(--positive)' }}>{j.affirmed_count}</strong></span>
                      <span>Pending: <strong style={{ color: j.pending_count > 0 ? 'var(--warning)' : 'var(--text-hint)' }}>{j.pending_count}</strong></span>
                      <span>In scope: <strong>{j.total_in_scope}</strong></span>
                      {j.total_in_scope > 0 && (
                        <div style={{ flex: 1, marginLeft: 12, height: 6, background: 'var(--border-subtle)', borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, height: '100%', background: 'var(--positive)' }} />
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: 12, fontStyle: 'italic', color: 'var(--text-primary)', padding: 8, background: 'var(--bg-hover)', borderRadius: 4, marginBottom: 6 }}>{j.narrative}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-hint)' }}>Sources: {j.source_attestations.join(', ')}</div>
                  </div>
                );
              })}
              <div style={{ fontSize: 13, fontWeight: 700, marginTop: 24, marginBottom: 10 }}>Transition Practical Expedients (ASC 606-10-50-7)</div>
              {judgments.transition_expedients.map((e) => (
                <div key={e.key} style={{ padding: 12, marginBottom: 8, border: '1px solid var(--border-subtle)', borderRadius: 6, background: 'var(--bg-card)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <div style={{ fontSize: 12, fontWeight: 700 }}>{e.title}</div>
                    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: e.elected ? 'rgba(34,197,94,0.12)' : 'var(--bg-hover)', color: e.elected ? 'var(--positive)' : 'var(--text-hint)' }}>
                      {e.elected ? 'ELECTED' : 'NOT ELECTED'}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--accent)', marginBottom: 4 }}>{e.paragraph_ref}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{e.narrative}</div>
                </div>
              ))}
            </>
          ) : null}
        </>
      )}
    </PageLayout>
  );
}
